function debounce(func, ms) {
  var timeout = void 0;
  return function () {
    clearTimeout(timeout);
    timeout = setTimeout(function () {
      timeout = null;
      func();
    }, ms);
  };
}

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input.js-search-input");
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    const resetIsHidden = this.resetButton.classList.contains("hidden");
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = "";
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define("search-form", SearchForm);

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.clearButtons = this.querySelectorAll(".js-form-clear");
    this.internalInput = this.querySelector("#PredictiveSearch-Input");
    this.internalClearButton = this.querySelector("#PredictiveSearch-Clear");

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    this.addEventListener("click", (event) => {
      if (event.target.closest(".predictive-search__form-submit")) {
        this.input.form.submit();
      } else if (event.target.closest(".js-modal-close")) {
        this.close(true);
        document
          .querySelector(".header")
          .classList.remove("header--search-active");
        if (!document.body.classList.contains("drawer--active")) {
          document.body.classList.remove("blur");
        }
      }
    });

    this.clearButtons.forEach((btn) => {
      btn.addEventListener("click", (event) => {
        event.preventDefault();

        this.input.value = "";
        this.searchTerm = "";
        this.predictiveSearchResults.innerHTML = "";
        this.removeAttribute("results");
        this.input.setAttribute("aria-expanded", "false");

        this.toggleResetButton();
        this.input.focus();
      });
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    this.getSearchResults(this.searchTerm);
    this.querySelector(".js-search-input")?.focus();
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    const results = this.querySelector(".predictive-search__results-wrapper");
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        if (window.innerWidth < 1200 && results) return;
        this.close();
        document
          .querySelector(".header")
          .classList.remove("header--search-active");
        if (!document.body.classList.contains("drawer--active")) {
          document.body.classList.remove("blur");
        }
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }

    if (event.code === "Enter") {
      event.preventDefault();
      this.submitSearch();
    }
  }

  submitSearch() {
    const query = this.input.value.trim();
    if (!query) return;

    window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(query)}`;
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    let urlToFetch = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;

    if (!searchTerm.length) {
      urlToFetch = `${routes.root_url}?section_id=predictive-search`;
    }

    fetch(urlToFetch, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);
        this.handleInternalSearchBar();
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  handleInternalSearchBar() {
    const internalInput = this.querySelector("#PredictiveSearch-Input");
    const internalClearButton = this.querySelector("#PredictiveSearch-Clear");

    if (internalInput && !internalInput.hasAttribute("data-bound")) {
      internalInput.setAttribute("data-bound", "true");

      internalInput.addEventListener(
        "input",
        debounce(() => {
          // Treat internal search like main search
          this.input.value = internalInput.value;
          this.onChange();
        }, 300),
      );
    }

    if (
      internalClearButton &&
      !internalClearButton.hasAttribute("data-bound")
    ) {
      internalClearButton.setAttribute("data-bound", "true");

      internalClearButton.addEventListener("click", () => {
        // Same behavior as close button
        this.close(true);
      });
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    // this.setLiveRegionResults();
    this.open();

    const searchTabs = this.querySelector("search-tabs");
    if (searchTabs) {
      const buttons =
        this.querySelector("search-tabs")?.querySelectorAll("button");
      const allTabs = this.querySelectorAll(
        ".predictive-search__results-content .predictive-search-tab",
      );
      if (buttons.lenght <= 1) return;
      buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          buttons.forEach((btn) => btn.classList.remove("active"));
          button.classList.add("active");
          const tabToOpen = event.currentTarget.getAttribute(
            "data-search-results",
          );

          allTabs.forEach((tab) => {
            if (tab.dataset.searchResults === tabToOpen) {
              tab.setAttribute("aria-selected", "true");
              tab.setAttribute("aria-hidden", "false");
            } else {
              tab.setAttribute("aria-selected", "false");
              tab.setAttribute("aria-hidden", "true");
            }
          });
        });
      });
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    const header = document.querySelector(".section-header");
    if (window.innerWidth >= 1200) {
      this.resultsMaxHeight = header
        ? window.innerHeight -
          document.querySelector(".section-header").getBoundingClientRect()
            .bottom
        : window.innerHeight - 100;
    } else {
      this.resultsMaxHeight = header
        ? window.innerHeight -
          document
            .querySelector(".header__search--mobile-tablet")
            .getBoundingClientRect().bottom
        : window.innerHeight - 100;
    }

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    // disable scroll on body
    document.body.style.overflow = "hidden";
    document.body.classList.add("blur");
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    // enable scroll on body
    document.body.style.overflow = "";
    document.body.classList.remove("blur");
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
